import { GanttConfig } from '../../GanttConfig';

const MIN_NODE_WIDTH = GanttConfig.SINGLE_ELEMENT_WIDTH / 2;

export type Point = ReturnType<typeof getGanttDepsRelationPoints>;

export const getGraphElementById = (id?: string | number): HTMLElement | null =>
  id ? document.querySelector(`[data-deps-graph-${id}]`) : null;

export const getQueueElementById = (id: string | number): HTMLElement | null =>
  document.querySelector(`[data-queue-id="${id}"]`);

const TIMELINE_HEIGHT = 66;

const getGanttDepsOriginEl = (element?: HTMLElement): HTMLElement | null => {
  if (typeof document === 'undefined') {
    return null;
  }
  return (
    (element?.closest('[data-gantt-deps-origin]') as HTMLElement | null) ??
    document.querySelector('[data-gantt-deps-origin]')
  );
};

/**
 * Horizontal center of `element` in the same coordinate system as the dependency SVG
 * (`absolute inset-0` under `[data-gantt-deps-origin]`). Uses bounding rects because task
 * nodes sit inside `relative h-full` wrappers where `offsetLeft` is always 0 while the
 * real position comes from transforms on ancestors.
 */
const getMiddleTaskX = (element: HTMLElement, nodeWidth: number, leftOffset?: number): number => {
  const origin = getGanttDepsOriginEl(element);
  const resolvedLeftOffset = leftOffset || 0;
  if (origin) {
    const originRect = origin.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    return elementRect.left - originRect.left + elementRect.width / 2 - resolvedLeftOffset;
  }
  return element.offsetLeft - resolvedLeftOffset + (nodeWidth < MIN_NODE_WIDTH ? MIN_NODE_WIDTH : nodeWidth) / 2;
};

/** Left or right edge of a queue row in chart coordinates (for fallback polylines). */
export const getGanttQueueEdgeXInChart = (
  queueElement: HTMLElement,
  edge: 'left' | 'right',
  leftOffset?: number,
): number => {
  const origin = getGanttDepsOriginEl(queueElement);
  const resolvedLeftOffset = leftOffset || 0;
  if (origin) {
    const originRect = origin.getBoundingClientRect();
    const queueRect = queueElement.getBoundingClientRect();
    return (
      (edge === 'left' ? queueRect.left - originRect.left : queueRect.right - originRect.left) - resolvedLeftOffset
    );
  }
  return (edge === 'left' ? 0 : queueElement.clientWidth) - resolvedLeftOffset;
};

export const getGanttDepsRelationPoints = (
  element: HTMLElement,
  queueIndex: number,
  nodeWidth: number,
  leftOffset?: number,
) => {
  if (element) {
    const queueBorderHeight = queueIndex;
    const middleTask = getMiddleTaskX(element, nodeWidth, leftOffset);
    const startYTop = queueIndex * 60 + queueBorderHeight + TIMELINE_HEIGHT;
    const startYBottom = startYTop + 60;
    return {
      middleTask,
      startYTop,
      startYBottom,
    };
  }
  return {
    middleTask: 0,
    startYTop: 0,
    startYBottom: 0,
  };
};
